import { z } from 'zod';

// Product Profile Schema (Main product information)
export const ProductProfileSchema = z.object({
  // Core product fields
  rowId: z.number().optional(),
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  productCode: z
    .string()
    .max(50, 'Product code must be less than 50 characters')
    .optional(),
  productId: z.number().optional(),
  parentProductName: z
    .string()
    .max(100, 'Parent product name must be less than 100 characters')
    .optional(),
  // Vendor fields
  vendorId: z.number().optional(),
  vendorName: z
    .string()
    .max(100, 'Vendor name must be less than 100 characters')
    .optional(),
  venProdCode: z
    .string()
    .max(50, 'Vendor product code must be less than 50 characters')
    .optional(),

  // Product classification
  type: z.string().max(50, 'Type must be less than 50 characters').optional(),
  subType: z
    .string()
    .max(50, 'Subtype must be less than 50 characters')
    .optional(),
  category: z
    .string()
    .max(50, 'Category must be less than 50 characters')
    .optional(),
  subCategory: z
    .string()
    .max(50, 'Subcategory must be less than 50 characters')
    .optional(),

  // Dates
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  nextAvlDate: z.string().optional().nullable(),

  // Pricing and financial
  standardPrice: z
    .number()
    .min(0, 'Standard price must be non-negative')
    .optional(),
  duty: z.number().min(0, 'Duty must be non-negative').optional(),
  marginType: z
    .string()
    .max(50, 'Margin type must be less than 50 characters')
    .optional(),

  // Product details
  barcode: z
    .string()
    .max(50, 'Barcode must be less than 50 characters')
    .optional(),
  status: z
    .string()
    .max(50, 'Status must be less than 50 characters')
    .optional(),
  uom: z.string().max(20, 'UOM must be less than 20 characters').optional(),
  capacity: z
    .string()
    .max(50, 'Capacity must be less than 50 characters')
    .optional(),

  // Descriptions
  description: z
    .string()
    .max(500, 'Description must be less than 500 characters')
    .optional(),
  longDescription: z
    .string()
    .max(2000, 'Long description must be less than 2000 characters')
    .optional(),
  richDescription: z
    .string()
    .max(5000, 'Rich description must be less than 5000 characters')
    .optional(),

  // Flags
  activeFlag: z.boolean().optional(),
  allocationOnly: z.boolean().optional(),
  orderable: z.boolean().optional(),
  taxable: z.boolean().optional(),

  // Manufacturer
  manufacturerId: z.number().optional(),
  manufacturerName: z
    .string()
    .max(100, 'Manufacturer name must be less than 100 characters')
    .optional(),
  manuProductCode: z
    .string()
    .max(50, 'Brand product code must be less than 50 characters')
    .optional(),

  // Other product fields
  parRowId: z.number().optional(),
  priceListId: z.number().optional(),
  quantity: z.number().min(0, 'Quantity must be non-negative').optional(),
  picLoc: z
    .string()
    .max(200, 'Picture location must be less than 200 characters')
    .optional(),
  averageCost: z
    .number()
    .min(0, 'Average cost must be non-negative')
    .optional(),
  height: z.number().min(0, 'Height must be non-negative').optional(),
  width: z.number().min(0, 'Width must be non-negative').optional(),
  length: z.number().min(0, 'Length must be non-negative').optional(),
  weight: z.number().min(0, 'Weight must be non-negative').optional(),
  netWeight: z.number().min(0, 'Net weight must be non-negative').optional(),
  imageFormatOk: z
    .string()
    .max(10, 'Image format must be less than 10 characters')
    .optional(),
  displayDetail: z
    .string()
    .max(200, 'Display detail must be less than 200 characters')
    .optional(),
  bottleSize: z
    .string()
    .max(50, 'Bottle size must be less than 50 characters')
    .optional(),
  vintage: z
    .string()
    .max(20, 'Vintage must be less than 20 characters')
    .optional(),
  wineColour: z
    .string()
    .max(30, 'Wine colour must be less than 30 characters')
    .optional(),
  grape: z
    .string()
    .max(100, 'Grape must be less than 100 characters')
    .optional(),
  country: z
    .string()
    .max(50, 'Country must be less than 50 characters')
    .optional(),
  region: z
    .string()
    .max(100, 'Region must be less than 100 characters')
    .optional(),
  producerId: z.number().optional(),
  alcohol: z
    .number()
    .min(0, 'Alcohol must be non-negative')
    .max(100, 'Alcohol must be less than 100%')
    .optional(),
  quality: z
    .string()
    .max(50, 'Quality must be less than 50 characters')
    .optional(),
  value: z
    .string()
    .max(100, 'Value must be less than 100 characters')
    .optional(),
  foodMatches: z
    .string()
    .max(500, 'Food matches must be less than 500 characters')
    .optional(),
  cellarFor: z
    .string()
    .max(100, 'Cellar for must be less than 100 characters')
    .optional(),
  maturation: z
    .string()
    .max(200, 'Maturation must be less than 200 characters')
    .optional(),
  processTime: z
    .string()
    .max(100, 'Process time must be less than 100 characters')
    .optional(),
  oakType: z
    .string()
    .max(100, 'Oak type must be less than 100 characters')
    .optional(),
  viticulture: z
    .string()
    .max(200, 'Viticulture must be less than 200 characters')
    .optional(),
  wineType: z
    .string()
    .max(50, 'Wine type must be less than 50 characters')
    .optional(),
  village: z
    .string()
    .max(100, 'Village must be less than 100 characters')
    .optional(),
  wineMaker: z
    .string()
    .max(100, 'Wine maker must be less than 100 characters')
    .optional(),
  closure: z
    .string()
    .max(50, 'Closure must be less than 50 characters')
    .optional(),
  glCategoryId: z.number().optional(),
  liquid: z.number().min(0, 'Liquid must be non-negative').optional(),
  dangerousGoods: z.boolean().optional(),
  imageType: z
    .string()
    .max(20, 'Image type must be less than 20 characters')
    .optional(),
  cubic: z.number().min(0, 'Cubic must be non-negative').optional(),
  smallImage: z
    .string()
    .max(200, 'Small image must be less than 200 characters')
    .optional(),
  largeImage: z
    .string()
    .max(200, 'Large image must be less than 200 characters')
    .optional(),
  totalInWh: z
    .number()
    .min(0, 'Total in warehouse must be non-negative')
    .optional(),
  onOrderNow: z.number().min(0, 'On order now must be non-negative').optional(),
  allocatedNow: z
    .number()
    .min(0, 'Allocated now must be non-negative')
    .optional(),
  location: z
    .string()
    .max(100, 'Location must be less than 100 characters')
    .optional(),
  createdBy: z.number().optional(),
  updatedBy: z.number().optional(),
});

// Stock Control Schema
export const StockControlSchema = z.object({
  available: z.number().optional(),
  inWh: z.number().optional(),
  allocated: z.number().optional(),
  pending: z.number().optional(),
  backOrder: z.number().optional(),
  onOrder: z.number().optional(),
});

// Replenishment Profile Schema
export const ReplenishmentSchema = z.object({
  waterLevel: z.number().optional(),
  reorderQuantity: z.number().optional(),
  orderQtyMultiple: z.number().optional(),
});

// GL Account Codes Schema
export const GLAccountsSchema = z.object({
  glAssetAcntName: z
    .string()
    .max(200, 'Asset account name must be less than 200 characters')
    .optional(),
  glExpAcntName: z
    .string()
    .max(200, 'Expense account name must be less than 200 characters')
    .optional(),
  glIncAcntName: z
    .string()
    .max(200, 'Income account name must be less than 200 characters')
    .optional(),
  glFinanceCatName: z
    .string()
    .max(200, 'Finance category name must be less than 200 characters')
    .optional(),
  glAssetAcntId: z.number().optional(),
  glExpAcntId: z.number().optional(),
  glIncAcntId: z.number().optional(),
});

// Extra Details Schema
export const ExtraDetailsSchema = z.object({
  extraDetail: z
    .string()
    .max(5000, 'Extra detail must be less than 5000 characters')
    .optional(),
});

// Audit Profile Schema
export const AuditSchema = z.object({
  // Location fields
  ownerLocId: z.number().optional(),
  ownerLocName: z
    .string()
    .max(100, 'Owner location name must be less than 100 characters')
    .optional(),

  // Audit fields
  created: z.string().optional(),
  createdName: z
    .string()
    .max(100, 'Created by name must be less than 100 characters')
    .optional(),
  updated: z.string().optional(),
  updatedName: z
    .string()
    .max(100, 'Updated by name must be less than 100 characters')
    .optional(),
});

// Main Product Details Schema - combines all sections
export const ProductDetailsSchema = z.object({
  productProfile: ProductProfileSchema,
  stockControl: StockControlSchema,
  replenishment: ReplenishmentSchema,
  glAccounts: GLAccountsSchema,
  extraDetails: ExtraDetailsSchema,
  auditProfile: AuditSchema,
});

// Type definitions inferred from schemas
export type ProductProfileData = z.infer<typeof ProductProfileSchema>;
export type StockControlData = z.infer<typeof StockControlSchema>;
export type ReplenishmentData = z.infer<typeof ReplenishmentSchema>;
export type GLAccountsData = z.infer<typeof GLAccountsSchema>;
export type ExtraDetailsData = z.infer<typeof ExtraDetailsSchema>;
export type AuditProfileData = z.infer<typeof AuditSchema>;
export type ProductFormData = z.infer<typeof ProductDetailsSchema>;

// Schema for form submission (more strict validation)
export const ProductSubmissionSchema = z.object({
  productProfile: ProductProfileSchema.extend({
    name: z.string().min(1, 'Name is required'),
  }),
  stockControl: StockControlSchema,
  replenishment: ReplenishmentSchema,
  glAccounts: GLAccountsSchema,
  extraDetails: ExtraDetailsSchema,
  auditProfile: AuditSchema,
});

export type ProductSubmissionData = z.infer<typeof ProductSubmissionSchema>;
